import { IScheduler } from "../utils/scheduler";
import * as logger from "../utils/logger";

/**
 * @member name: The name of the task to be registered with the scheduler.
 * @member msInterval: The interval in milliseconds at which the task should run.
 * @member hasDelayAfterStart: Whether the task should have a delay after scheduler.startTask is called.
 * @member registerOnceForAllUsers: Whether the task should be registered once for all users.
 * @member onDemand: Whether the task should be run only once and not scheduled at a regular interval when start is called.
 * @member startingInterval: The delay in milliseconds before the task should start.
 * @member delayByInterval: If hasDelayAfterStart, then whether the task should be delayed by msInterval + startingDelay.
 */
export interface ITaskOptions {

    /**
     * The name of the task to be registered with the scheduler
     */
    readonly name: string;

    /**
     * The interval in milliseconds at which the task should run
     */
    readonly msInterval: number;

    /**
     * Whether the task should have a delay after scheduler.startTask is called
     */
    readonly hasDelayAfterStart?: boolean;

    /**
     * Whether the task should be registered once for all users
     */
    readonly registerOnceForAllUsers?: boolean;

    /**
     * Whether the task should be run only once and not scheduled at a regular interval when start is called.
     */
    readonly onDemand?: boolean;

    /**
     * The delay in milliseconds before the task should start
     */
    readonly startingInterval?: number;

    /**
     * If hasDelayAfterStart, then whether the task should be delayed by msInterval + startingDelay
     */
    readonly delayByInterval?: boolean;
}

export interface IService {
    /**
     * The options for the task to be registered with the scheduler.
     * See {@link ITaskOptions} for more details.
     */
    readonly options: ITaskOptions;

    /**
     * The name of the user for which the task is registered.
     */
    readonly userName: string | undefined;

    /**
     * @returns Whether the task is registered with the scheduler
     */
    isRegistered: () => boolean;

    /**
     * Register a method with the scheduler
     * @param task - The method to be registered with the scheduler
     */
    registerTask: (task: (...args: any[]) => void) => void;

    /**
     * Unregister the method registered with the scheduler
     */
    unregisterTask: () => void;

    /**
     * Start the method that has been registered with the Scheduler.
     */
    startTask: () => void;

    /**
     * Stop the method that has been registered with the Scheduler.
     */
    stopTask: () => void;

    /**
     * Start the subscriptions for this service
     */
    startSubscriptions: () => void;

    /**
     * Stop the subscriptions for this service
     */
    stopSubscriptions: () => void;

    /**
     * Clean up
     */
    destroy(): void;
}

export abstract class Service implements IService{

    readonly options: ITaskOptions = {
        name: "@default",
        msInterval: 5 * 1000,
        hasDelayAfterStart: false,
        registerOnceForAllUsers: false,
        onDemand: false,
        startingInterval: 0,
        delayByInterval: false
    };

    userName: string | undefined = undefined;

    protected scheduler: IScheduler | undefined;

    constructor(scheduler?: IScheduler) {
        this.scheduler = scheduler;
    }

    isRegistered(): boolean {
        if (!this.scheduler) {
            return false;
        }
        return this.scheduler.isTaskRegistered(this.options.name, this.userName);
    }

    registerTask(task: (...args: any[]) => void, options: ITaskOptions = this.options, userName: string | undefined = this.userName): void {
        if (!this.scheduler) {
            logger.error(logger.Source.SERVICES, `Task ${ this.options.name } for user ${ this.userName } is trying to register without a defined Scheduler.`);
            return;
        }
        this.scheduler.registerTaskWithOptions(
            task,
            options,
            userName
        );
    }

    unregisterTask(): void {
        if (!this.scheduler) {
            logger.error(logger.Source.SERVICES, `Task ${ this.options.name } for user ${ this.userName } is trying to unregister without a defined Scheduler.`);
            return;
        }
        this.scheduler.unregisterTask(this.options.name, this.userName);
    }

    startTask(): void {
        if (this.isRegistered()) {
            this.scheduler?.start(this.options.name, this.userName);
            return;
        }
        if (this.scheduler) {
            // We only want to log a warning if the service is instantiated with a scheduler. Because if no scheduler is provided
            // to the constructor then it's likely that the service only wants to subscribe to events and not run on a schedule.
            logger.log(logger.Source.SERVICES, `Warning - Attempting to start task ${ this.options.name } for user ${ this.userName } without registration.`);
        }
     }

    stopTask(): void {
        if (this.isRegistered()) {
            this.scheduler?.stop(this.options.name, this.userName);
            return;
        }
        if (this.scheduler) {
            // We only want to log a warning if the service is instantiated with a scheduler. Because if no scheduler is provided
            // to the constructor then it's likely that the service only wants to subscribe to events and not run on a schedule.
            logger.log(logger.Source.SERVICES, `Warning - Attempting to stop task ${ this.options.name } for user ${ this.userName } without registration.`);
        }
    }

    startSubscriptions(): void {
        /**
         * This is a default implemetation made to satisfy interface requirements. Subclasses that wish to subscribe
         * to events should override this method with the code that sets up the subscriptions. See a service
         * implementation for concrete details.
         */
        // Do nothing
    }

    stopSubscriptions(): void {
        /**
         * This is a default implemetation made to satisfy interface requirements. Subclasses that wish to subscribe
         * to events should override this method with the code that removes subscriptions. See a service
         * implementation for concrete details.
         */
        // Do nothing
    }

    destroy() {
        this.stopSubscriptions();
        this.unregisterTask();
    }
}
